import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { DynamicFilterConfig, createDynamicFilterState } from '@card-software/library';
import { TranslateService } from '@ngx-translate/core';
import { PaginatorState } from 'primeng/paginator';
import { finalize } from 'rxjs';
import { ApAdvancePayment, ApAdvancePaymentSearchCommand, ApPaymentStatus } from '../../../models/ap-advance/ap-advance';
import { ApAdvanceProxyService } from '../../../proxies/ap-advance.proxy.service';
import { EnumService } from '../../../services/enum.service';
import { ProviderFilterService } from '../../../services/provider-filter.service';

@Component({
  selector: 'app-ap-advances',
  templateUrl: './ap-advances.component.html',
  styleUrl: './ap-advances.component.scss',
})
export class ApAdvancesComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly service = inject(ApAdvanceProxyService);
  private readonly enumService = inject(EnumService);
  private readonly providerFilter = inject(ProviderFilterService);
  private readonly translateService = inject(TranslateService);

  isLoading = signal(0);
  advances = signal<ApAdvancePayment[]>([]);
  totalRecords = signal(0);
  currentPage = signal(1);
  pageSize = signal(25);

  filterConfig = signal<DynamicFilterConfig | null>(null);
  showFilters = signal(true);
  readonly filterState = createDynamicFilterState({ storageKey: 'ap-advances-filters', config: this.filterConfig });

  private statusLabels: Record<number, string> = {};
  private currencyLabels: Record<number, string> = {};
  /** Criteria of the current result set — replayed on paging so a page change keeps the filters. */
  private lastSearchCriteria: ApAdvancePaymentSearchCommand = {};

  /** This screen's own type-ahead state — see ProviderFilterService.createConfig. */
  private readonly providerAutocompleteConfig = this.providerFilter.createConfig(inject(DestroyRef));

  constructor() {
    this.filterState.applied$
      .pipe(takeUntilDestroyed())
      .subscribe((filters) => this.searchWithFilters(filters as ApAdvancePaymentSearchCommand));
  }

  ngOnInit(): void {
    this.statusLabels = this.enumService.getEnum('ApPaymentStatus').values as Record<number, string>;
    this.currencyLabels = this.enumService.getEnum('Currency').values as Record<number, string>;

    this.filterConfig.set(this.buildFilterConfig());
  }

  private startLoading(): void {
    this.isLoading.update((v) => v + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((v) => v - 1);
  }

  private translate(key: string, fallback: string): string {
    const translation = this.translateService.instant(key);
    return translation === key ? fallback : translation;
  }

  private buildFilterConfig(): DynamicFilterConfig {
    return {
      persistInUrl: true,
      persistVisibility: true,
      filters: [
        {
          type: 'text',
          formControlName: 'number',
          label: this.translate('ApAdvance_Number', 'Advance #'),
          placeholder: this.translate('ApAdvances_Search', 'Enter advance number'),
          defaultVisible: true,
        },
        {
          type: 'autocomplete',
          formControlName: 'providerReference',
          label: this.translate('ApPayment_Provider', 'Provider'),
          placeholder: this.translate('Provider_Search', 'Search provider'),
          autocompleteConfig: this.providerAutocompleteConfig,
          showClear: true,
          defaultVisible: true,
        },
        {
          type: 'select',
          formControlName: 'status',
          label: this.translate('Status', 'Status'),
          placeholder: this.translate('Select_status', 'Select status'),
          options: this.statusLabels,
          showClear: true,
          defaultVisible: true,
        },
        {
          type: 'date',
          formControlName: 'paymentDateFrom',
          label: this.translate('ApAdvance_DateFrom', 'Date from'),
          defaultVisible: false,
        },
        {
          type: 'date',
          formControlName: 'paymentDateTo',
          label: this.translate('ApAdvance_DateTo', 'Date to'),
          defaultVisible: false,
        },
      ],
    };
  }

  private search(criteria: ApAdvancePaymentSearchCommand): void {
    this.lastSearchCriteria = criteria;
    this.startLoading();
    this.service
      .search(criteria, this.currentPage(), this.pageSize())
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          this.advances.set(response?.data?.items ?? []);
          this.totalRecords.set(response?.data?.totalCount ?? 0);
        },
        error: () => {
          this.advances.set([]);
          this.totalRecords.set(0);
        },
      });
  }

  private searchWithFilters(filters: ApAdvancePaymentSearchCommand): void {
    // A persisted/URL filter arrives as a string — the API types status as an enum and the dates as ISO, so
    // coerce both back before they leave the page.
    const criteria: ApAdvancePaymentSearchCommand = {
      ...filters,
      number: filters.number || null,
      // The type-ahead puts the whole suggestion in the control, so the spread above would
      // send an object where the API expects the id.
      providerReference: this.providerFilter.toProviderId(filters.providerReference),
      status: filters.status != null ? (Number(filters.status) as ApPaymentStatus) : null,
      paymentDateFrom: this.toIso(filters.paymentDateFrom),
      paymentDateTo: this.toIso(filters.paymentDateTo),
    };
    this.currentPage.set(1);
    this.search(criteria);
  }

  private toIso(value: unknown): string | null {
    if (!value) return null;
    return value instanceof Date ? value.toISOString() : String(value);
  }

  onPageChange(event: PaginatorState): void {
    this.currentPage.set((event.page ?? 0) + 1);
    this.pageSize.set(event.rows ?? this.pageSize());
    this.search(this.lastSearchCriteria);
  }

  redirectToDetails(id?: string): void {
    this.router.navigate(['/ap-advance-details'], { queryParams: { id: id ?? 'new' } });
  }

  statusLabel(status: number): string {
    return this.statusLabels[status] ?? '';
  }

  statusSeverity(status: number): 'success' | 'info' | 'secondary' {
    if (status === ApPaymentStatus.Posted) return 'info';
    return 'secondary';
  }

  currencyLabel(currency: number | null | undefined): string {
    if (currency === null || currency === undefined) return '';
    return this.currencyLabels[currency] ?? '';
  }

  getRowData(rowData: unknown): ApAdvancePayment {
    return rowData as ApAdvancePayment;
  }
}
